import { Mail, Linkedin, Instagram, Facebook, Clock } from "lucide-react";
import LeadForm from "@/components/LeadForm";
import useSeo from "@/lib/useSeo";

const XIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

export default function Contact() {
  useSeo({
    title: "Contact ClariDent RCM | Talk to a Dental RCM Specialist",
    description: "Contact ClariDent RCM about dental billing, insurance verification, denial management, and AR recovery. Email Billing@claridentrcm.com — a real specialist reads every message.",
    path: "/contact",
  });
  return (
    <main data-testid="contact-page">
      <section className="bg-slate-50 py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <p className="text-xs tracking-[0.25em] uppercase font-bold text-teal-600 mb-4">Contact Us</p>
          <h1 className="font-display text-5xl sm:text-6xl tracking-tighter font-black text-navy-900" data-testid="contact-heading">
            Let's talk about <span className="text-teal-600">your revenue.</span>
          </h1>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-20 grid lg:grid-cols-2 gap-16">
        <div>
          <h2 className="font-display text-3xl font-bold text-navy-900">Reach the ClariDent team</h2>
          <p className="mt-4 text-base text-slate-600 leading-relaxed">
            Questions about pricing, onboarding, or whether outsourcing is right for your practice? Send us a message — a real specialist reads every one.
          </p>
          <div className="mt-10 space-y-6">
            <a href="mailto:Billing@claridentrcm.com" className="flex items-center gap-4 group" data-testid="contact-email-link">
              <span className="h-12 w-12 rounded-xl bg-teal-50 flex items-center justify-center group-hover:bg-teal-600 transition-colors duration-300">
                <Mail size={22} className="text-teal-600 group-hover:text-white transition-colors duration-300" />
              </span>
              <span>
                <span className="block text-xs font-bold uppercase tracking-widest text-slate-400">Email</span>
                <span className="block font-semibold text-navy-900">Billing@claridentrcm.com</span>
              </span>
            </a>
            <div className="flex items-center gap-4">
              <span className="h-12 w-12 rounded-xl bg-teal-50 flex items-center justify-center">
                <Clock size={22} className="text-teal-600" />
              </span>
              <span>
                <span className="block text-xs font-bold uppercase tracking-widest text-slate-400">Response Time</span>
                <span className="block font-semibold text-navy-900">Within one business day</span>
              </span>
            </div>
          </div>
          <div className="mt-10">
            <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-4">Follow ClariDent RCM</p>
            <div className="flex gap-4">
              <a href="https://www.linkedin.com/company/claridentrcm" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" data-testid="contact-linkedin" className="h-12 w-12 rounded-full border border-slate-200 flex items-center justify-center text-navy-900 hover:bg-teal-600 hover:text-white hover:border-teal-600 transition-colors duration-200">
                <Linkedin size={20} />
              </a>
              <a href="https://www.instagram.com/claridentrcm" target="_blank" rel="noopener noreferrer" aria-label="Instagram" data-testid="contact-instagram" className="h-12 w-12 rounded-full border border-slate-200 flex items-center justify-center text-navy-900 hover:bg-teal-600 hover:text-white hover:border-teal-600 transition-colors duration-200">
                <Instagram size={20} />
              </a>
              <a href="https://www.facebook.com/claridentrcm" target="_blank" rel="noopener noreferrer" aria-label="Facebook" data-testid="contact-facebook" className="h-12 w-12 rounded-full border border-slate-200 flex items-center justify-center text-navy-900 hover:bg-teal-600 hover:text-white hover:border-teal-600 transition-colors duration-200">
                <Facebook size={20} />
              </a>
              <a href="https://x.com/claridentrcm" target="_blank" rel="noopener noreferrer" aria-label="X (Twitter)" data-testid="contact-x" className="h-12 w-12 rounded-full border border-slate-200 flex items-center justify-center text-navy-900 hover:bg-teal-600 hover:text-white hover:border-teal-600 transition-colors duration-200">
                <XIcon size={18} />
              </a>
            </div>
          </div>
        </div>
        <div className="rounded-3xl border border-slate-200 bg-white p-8 sm:p-10 shadow-sm" data-testid="contact-form-card">
          <h3 className="font-display text-2xl font-bold text-navy-900 mb-6">Send us a message</h3>
          <LeadForm type="contact" testIdPrefix="contact-form" />
        </div>
      </section>
    </main>
  );
}
